/**
 * W47 (2026-05-17) — WatchlistBadgeLegend
 *
 * Kollabierbare Legende ueber der Watchlist, die alle 4 Badge-Typen in
 * Klartext-Deutsch erklaert. Default kollabiert — User kann mit Klick aufklappen.
 *
 * Beim ersten Besuch (kein cookie/localStorage Flag) ist sie aufgeklappt.
 */

import { useEffect, useState } from 'react';
import {
  ChevronDown,

  Coins,
  HelpCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'cockpit.watchlist.badgeLegend.collapsed';

function loadCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function saveCollapsed(collapsed: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
  } catch {
    // ignore
  }
}

export function WatchlistBadgeLegend() {
  const [collapsed, setCollapsed] = useState<boolean>(true);
  useEffect(() => {
    setCollapsed(loadCollapsed());
  }, []);

  function toggle() {
    setCollapsed((c) => {
      const next = !c;
      saveCollapsed(next);
      return next;
    });
  }

  return (
    <div className="rounded-md border border-zinc-800/80 bg-zinc-900/30 mb-3">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-800/40 transition-colors"
        aria-expanded={!collapsed}
      >
        <span className="flex items-center gap-2">
          <HelpCircle className="size-3.5 text-zinc-500" />
          <span className="font-semibold">Was bedeuten die Symbole?</span>
          <span className="text-zinc-500 hidden sm:inline">
            ({collapsed ? 'klicken zum Aufklappen' : 'klicken zum Einklappen'})
          </span>
        </span>
        <ChevronDown
          className={cn(
            'size-4 text-zinc-500 transition-transform',
            !collapsed && 'rotate-180'
          )}
        />
      </button>

      {!collapsed && (
        <div className="px-3 pb-3 pt-1 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          {/* Stufe 1 der Score-Entkopplung (14.08.2026): Die Kacheln fuer
              Score-Trend und Ueberzeugung sind gestrichen — die Badges dazu
              gibt es in der Zeile nicht mehr. Eine Legende, die Dinge
              erklaert, die niemand sieht, stiftet nur Suchbilder. */}
          {/* Dividenden-Badge */}
          <div className="rounded border border-zinc-800/60 bg-zinc-950/30 p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-emerald-300 bg-emerald-500/15 border border-emerald-500/40 text-[11px] font-semibold">
                <Coins className="size-3" />
                3.2 %
              </span>
              <span className="text-zinc-500 text-[10px]">oder</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-emerald-300 bg-emerald-500/15 border border-emerald-500/40 text-[11px] font-semibold">
                <Coins className="size-3" />
                25J ★
              </span>
              <span className="text-zinc-400 font-medium ml-1">Dividende</span>
            </div>
            <p className="text-[11px] text-zinc-400 leading-snug">
              <strong>Dividenden-Rendite</strong> in % (TTM) oder <strong>Streak-Jahre</strong>{' '}
              fuer Dividenden-Aristokraten (25+ Jahre kontinuierliche Erhoehungen, mit Stern).
              Wert in <span className="text-violet-300">violett</span> = KI-Recherche (kein
              Provider-Wert verfuegbar). Tooltip zeigt naechstes Ex-Datum.
            </p>
          </div>

          {/* Tipp am Ende */}
          <div className="md:col-span-2 rounded border border-blue-500/30 bg-blue-950/20 p-2.5">
            <p className="text-[11px] text-blue-200 leading-snug">
              <strong>Tipp fuer Position-Trader:</strong> Die Zeile traegt den
              Zustand der Regel — <span className="font-mono">Zone offen</span> heisst
              handeln pruefen, <span className="font-mono">Boden reift</span> heisst
              beobachten. Bei Boden-Kandidaten gilt: erst bei Wende-Zeichen aktiv
              werden, nie ins fallende Messer.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
